// src/components/RecordServiceBottomSheet.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, ScrollView, StyleSheet, View, Text } from 'react-native';
import {
  Button,
  TextInput as PaperTextInput,
  Switch,
  useTheme,
} from 'react-native-paper';
import { DatePickerModal } from 'react-native-paper-dates';
import BaseBottomSheet from './BaseBottomSheet';
import { useAuth } from '../context/AuthContext';
import ServiceService from '../services/serviceService';
import { ServiceSchedule, CreateServiceRecordData } from '../types/Service';
import { ErrorLogger } from '../utils/errorLogger';
import { formatShortDate as formatDate } from '../utils/formatDate';

interface RecordServiceBottomSheetProps {
  visible: boolean;
  schedule: ServiceSchedule | null;
  onClose: () => void;
  onRecorded: () => void;
}

const RecordServiceBottomSheet: React.FC<RecordServiceBottomSheetProps> = ({
  visible,
  schedule,
  onClose,
  onRecorded,
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [performedDate, setPerformedDate] = useState(new Date());
  const [nextDueDate, setNextDueDate] = useState(new Date());
  const [cost, setCost] = useState('');
  const [notes, setNotes] = useState('');
  const [userMarkedUnexpected, setUserMarkedUnexpected] = useState(false);
  const [showPerformedPicker, setShowPerformedPicker] = useState(false);
  const [showNextDuePicker, setShowNextDuePicker] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reset and pre-fill when sheet opens
  useEffect(() => {
    if (!schedule || !visible) return;
    const today = new Date();
    setPerformedDate(today);
    const due = new Date(today);
    due.setDate(due.getDate() + schedule.intervalDays);
    setNextDueDate(due);
    setCost('');
    setNotes('');
    // Pre-check "unexpected" if the performed date is more than 10% early
    const msPerDay = 1000 * 60 * 60 * 24;
    const daysEarly = Math.floor(
      (schedule.nextDueDate.getTime() - today.getTime()) / msPerDay,
    );
    setUserMarkedUnexpected(daysEarly > schedule.intervalDays * 0.1);
  }, [schedule, visible]);

  const handlePerformedDateChange = useCallback((date: Date) => {
    setPerformedDate(date);
    if (!schedule) return;
    const due = new Date(date);
    due.setDate(due.getDate() + schedule.intervalDays);
    setNextDueDate(due);
  }, [schedule]);

  const handleSave = useCallback(async () => {
    if (!schedule?.id) return;
    if (!user?.uid) {
      Alert.alert('Error', 'You must be signed in to record a service.');
      return;
    }
    try {
      setSaving(true);
      const data: CreateServiceRecordData = {
        truckId: schedule.truckId,
        serviceScheduleId: schedule.id,
        serviceType: schedule.serviceType,
        performedDate,
        cost: cost ? parseFloat(cost) : undefined,
        notes: notes.trim() || undefined,
        userMarkedUnexpected,
        newNextDueDate: nextDueDate,
        intervalDays: schedule.intervalDays,
        currentNextDueDate: schedule.nextDueDate,
      };
      await ServiceService.recordService(user.uid, data);
      onRecorded();
      onClose();
    } catch (error) {
      ErrorLogger.logError(error as any, 'RecordServiceBottomSheet.handleSave');
      Alert.alert('Error', 'Failed to record service. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [schedule, user, performedDate, cost, notes, userMarkedUnexpected, nextDueDate, onRecorded, onClose]);


  if (!schedule) return null;

  return (
    <BaseBottomSheet
      visible={visible}
      onClose={onClose}
      title={`Record: ${schedule.serviceType}`}
      height={0.85}
    >
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <Text style={[styles.label, { color: theme.colors.onSurface }]}>Date Performed</Text>
        <Button
          mode="text"
          onPress={() => setShowPerformedPicker(true)}
          style={styles.dateButton}
          icon="calendar"
        >
          {formatDate(performedDate)}
        </Button>
        <DatePickerModal
          locale="en-GB"
          mode="single"
          visible={showPerformedPicker}
          onDismiss={() => setShowPerformedPicker(false)}
          date={performedDate}
          onConfirm={({ date }) => {
            setShowPerformedPicker(false);
            if (date) handlePerformedDateChange(date);
          }}
        />

        <View style={styles.fieldContainer}>
          <PaperTextInput
            mode="flat"
            label="Cost (UGX, optional)"
            value={cost}
            onChangeText={setCost}
            keyboardType="numeric"
            style={[styles.paperInput, { backgroundColor: theme.colors.surfaceVariant }]}
          />
        </View>

        <View style={styles.fieldContainer}>
          <PaperTextInput
            mode="flat"
            label="Notes (optional)"
            value={notes}
            onChangeText={setNotes}
            multiline
            numberOfLines={2}
            style={[styles.paperInput, { backgroundColor: theme.colors.surfaceVariant }]}
          />
        </View>

        <View style={[styles.switchRow, { backgroundColor: theme.colors.surfaceVariant }]}>
          <Text style={[styles.switchLabel, { color: theme.colors.onSurface }]}>
            Mark as Unexpected
          </Text>
          <Switch value={userMarkedUnexpected} onValueChange={setUserMarkedUnexpected} />
        </View>

        <Text style={[styles.label, { color: theme.colors.onSurface }]}>Next Due Date</Text>
        <Button
          mode="text"
          onPress={() => setShowNextDuePicker(true)}
          style={styles.dateButton}
          icon="calendar"
        >
          {formatDate(nextDueDate)}
        </Button>
        <DatePickerModal
          locale="en-GB"
          mode="single"
          visible={showNextDuePicker}
          onDismiss={() => setShowNextDuePicker(false)}
          date={nextDueDate}
          onConfirm={({ date }) => {
            setShowNextDuePicker(false);
            if (date) setNextDueDate(date);
          }}
        />
      </ScrollView>

      <View style={styles.buttonContainer}>
        <Button
          mode="contained"
          onPress={handleSave}
          loading={saving}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Record Service'}
        </Button>
      </View>
    </BaseBottomSheet>
  );
};

const styles = StyleSheet.create({
  scrollView: { flexShrink: 1 },
  scrollContent: { padding: 16 },
  label: { fontFamily: 'Inter-Medium', fontSize: 14, marginBottom: 8, marginTop: 12 },
  fieldContainer: { marginBottom: 8 },
  paperInput: {},
  dateButton: { marginBottom: 4 },
  switchRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    marginVertical: 4,
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  switchLabel: { fontFamily: 'Inter-Medium', fontSize: 16 },
  buttonContainer: { padding: 16 },
});

export default RecordServiceBottomSheet;
